<template>
  <form>
    <div class="d-flex flex-row nowrap justify-content-between mb-3 heading">
      <span>{{ t('loginRegister.login') }}</span>
    </div>

    <div class="my-3" v-if="props.isDisplayEmailSentMessage">
      <Banner :background="'var(--green-100)'">
        <template #text>
          <span class="caption text-primary-green">
            {{ t('loginRegister.user_info_email_sent') }}
            {{ ' ' }}
            {{ props.emailSentTo }}
          </span>
        </template>
      </Banner>
    </div>

    <div id="email" class="mb-3">
      <TextInput
        :id="'email'"
        :disabled="true"
        :value="props.email"
        :placeholder="contentProcessed['email']"
        :container-box-border="
          state.showErrors && invalidFields.has('email')
            ? '1px solid var(--primary-orange)'
            : undefined
        "
        @input="newVal => handleEmailChange(newVal)"
      >
        <template #after>
          <div
            v-if="!invalidFields.has('email') && props.email"
            class="mx-3 my-2"
          >
            <EditSvg
              :color="'var(--primary-blue)'"
              role="button"
              @click.stop="handleEditClick"
            />
          </div>
        </template>
      </TextInput>
      <FieldError
        class="my-1"
        ref="email-error"
        v-if="state.showErrors && invalidFields.has('email')"
      >
        <template v-if="!props.email">
          {{ t('loginRegister.user_info_mandatory_field') }}
        </template>
        <template v-else-if="!isEmailValid"> Email is not valid </template>
      </FieldError>
    </div>

    <div id="password" ref="passwordwrapper" class="mb-3">
      <TextInput
        :id="'password'"
        :value="loginForm.password"
        :placeholder="contentProcessed['password1']"
        :input-type="state.isSeePassword ? 'text' : 'password'"
        :container-box-border="
          state.showErrors && invalidFields.has('password')
            ? '1px solid var(--primary-orange)'
            : undefined
        "
        @input="newVal => handlePasswordChange(newVal)"
      >
        <template #after>
          <div
            v-if="loginForm.password"
            role="button"
            @click="() => handleToggleSeePassword()"
            class="mx-3 my-2"
          >
            <EyeSvg
              :opacity="state.isSeePassword ? 1 : 0.5"
              :color="'var(--primary-blue)'"
            />
          </div>
        </template>
      </TextInput>
      <FieldError v-if="state.showErrors && invalidFields.has('password')">
        <template v-if="!loginForm.password">
          {{ t('loginRegister.user_info_mandatory_field') }}
        </template>
      </FieldError>
    </div>

    <div
      class="my-3 link-small text-primary-blue"
      role="button"
      @click.stop="() => handleResetPassword()"
    >
      <span
        ><u>{{ t('loginRegister.user_info_forgot_password') }}</u></span
      >
    </div>

    <div class="my-3" v-if="isPasswordIncorrect">
      <Banner :background="'var(--orange-100)'">
        <template #text>
          <span class="caption text-primary-orange">{{
            t('loginRegister.contacts_incorrect_credentials')
          }}</span>
        </template>
      </Banner>
    </div>

    <div class="my-4">
      <Button
        :disabled="state.showErrors && invalidFields.size > 0"
        class="p-3"
        @buttonClick="handleButtonClick"
      >
        <template #text>
          <span>{{ t('loginRegister.validate') }}</span>
        </template>
      </Button>
    </div>
  </form>
</template>

<script lang="ts" setup>
  import {
    reactive,
    computed,
    ComputedRef,
    withDefaults,
    defineProps,
    defineEmits,
    ref,
    Ref,
    onMounted,
    onBeforeUnmount,
    onUnmounted,
    watch,
  } from 'vue'
  import scrollIntoView from 'smooth-scroll-into-view-if-needed'

  import TextInput from '@/components/LoginRegister/common/inputs/Text/v1/Text.vue'
  import FieldError from '@/components/LoginRegister/common/FieldError/v1/FieldError.vue'
  import Banner from '@/components/LoginRegister/common/Banner/v1/Banner.vue'
  import Button from '@/components/LoginRegister/common/Button/v1/Button.vue'
  import EyeSvg from '@/components/LoginRegister/svg/EyeSvg.vue'
  // import CheckSvg from '@/components/LoginRegister/svg/CheckSvg.vue'
  import EditSvg from '@/components/LoginRegister/svg/EditSvg.vue'

  import login_form_mandatory from '@/config/loginRegisterLegacy/login_form_mandatory.config'

  import useIsEmailValid from '@/composables/useIsEmailValid'

  import { onKeyStroke } from '@vueuse/core'

  import LoginForm from '@/model/Form/LoginForm'

  import * as _ from 'lodash'

  import login from '@/logic/contact/login'
  import useLoading, { isLoadingAny } from '@/composables/useLoading'
  import loginForm from '@/state/loginForm'

  import isPasswordIncorrect from '@/state/isPasswordIncorrect'
  import resetLoginForm from '@/logic/user-info/resetLoginForm'
  import { getQueryParams } from '@/utils'

  import { useI18n } from 'vue-i18n'

  const {t} = useI18n()

  interface Props {
    email: string
    isDisplayEmailSentMessage: boolean
    emailSentTo: string
  }
  interface Emits {
    (e: 'emailChange', email: string): void
    (e: 'resetPassword'): void
    (e: 'useOtherEmail'): void
  }

  // query params
  const queryParams = getQueryParams()

  // state
  const state = reactive({
    isSeePassword: false,
    showErrors: false,
  })

  // computed
  const contentProcessed = computed(() => {
    return {
      email: `${t("loginRegister.contacts_email")}${
        login_form_mandatory.has('email') ? '*' : ''
      }`,
      password1: `${t("loginRegister.contacts_password1")}${
        login_form_mandatory.has('password') ? '*' : ''
      }`,
    }
  })
  const invalidFields = computed(() => {
    const invalidFields = new Set<string>()
    login_form_mandatory.forEach(field => {
      const fieldKey = field as keyof LoginForm
      if (!loginForm.value[fieldKey]) {
        invalidFields.add(field)
      }
    })
    if (!isEmailValid.value) invalidFields.add('email')
    return invalidFields
  })
  const email: ComputedRef<string> = computed(() => props.email)
  const isEmailValid: ComputedRef<boolean> = useIsEmailValid(email)

  // ref
  const passwordwrapper = ref()

  // props
  const props = withDefaults(defineProps<Props>(), {})

  // emit
  const emit = defineEmits<Emits>()

  // handlers
  const handleButtonClick = async () => {
    if (isLoadingAny.value) return

    if (invalidFields.value.size > 0) {
      state.showErrors = true
      const selector = `#${[...invalidFields.value][0]}`
      const invalidFieldElement = document.querySelector(
        selector
      ) as HTMLElement
      await scrollIntoView(invalidFieldElement, {
        // scrollMode: 'if-needed',
        ease: t => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
        duration: 750,
        block: 'center',
        inline: 'nearest',
      })
      return
    }
    state.showErrors = false

    const { startLoadingThis, stopLoadingThis } = useLoading()
    startLoadingThis()
    await login(loginForm.value)

    stopLoadingThis()
  }
  const handleEmailChange = (email: string) => {
    emit('emailChange', email)
  }
  const handlePasswordChange = (password: string) => {
    const formClone = _.cloneDeep(loginForm.value)
    formClone.password = password
    loginForm.value = formClone
  }
  const handleToggleSeePassword = () => {
    state.isSeePassword = !state.isSeePassword
  }
  const handleResetPassword = () => {
    emit('resetPassword')
  }
  const handleEditClick = () => {
    emit('useOtherEmail')
  }


  onMounted(async () => {
    await resetLoginForm()

    // onKeyStroke
    const stop = onKeyStroke('Enter', () => {
      handleButtonClick()
    })
    onBeforeUnmount(() => stop())

    passwordwrapper?.value?.querySelector('input')?.focus()
  })

  onUnmounted(() => {
    // Reset the error when unmounting
    isPasswordIncorrect.value = false
  })

  watch(
    () => props.email,
    () => {
      const formClone = _.cloneDeep(loginForm.value)
      formClone.email = props.email
      loginForm.value = formClone
    }
  )
</script>

<style  scoped></style>
